import './booking-page.css';
import { useEffect, useRef, useState } from 'react';
import { OfflineBookingPage } from './components/offline-booking-page';
import { OnlineBookingPage } from './components/online-booking-page';
import { OpportunityComplete, CaseInfo, CaseStatus, Result, Slot, defaultCaseInfo, CallBodyRequest, SalesChannel, OpportunityAssignDto } from '../../dto/model';
import { useSharedLoadingGuard } from '../../hooks/use-loading-guard';
import { GetAccessToken, HasRole, Role } from '../../utils/auth-utils';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../authConfig';
import { bookingPageService } from '../../services/booking-page-service';
import { Header } from '../header/header';
import { HeaderBookingPage } from '../header/components/header-booking-page';
import { OnlineBookingNoCasesPage } from './components/online-booking-no-cases-page';
import { Alert, Button, Spin, App } from 'antd';
import { strings } from '../../lang';
import { ConfirmationPage } from './components/confirmation-page';
import CaseScript from './components/case-script';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { OnlineRecordingBookingPage } from './components/online-recording-booking-page';
import { salesAppointmentsService } from '../../services/sales-appointments-service';
import { useSalesChannelState } from '../../store/header-state';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import confetti from 'canvas-confetti';
import { PowerHourConfig, UserDataDto } from '../../dto/reporting-models';
import { reportingService } from '../../services/reporting-service';
import PowerHourBar from '../power-hour/power-hour-bar';
import { handleBeforeUnload } from '../../utils/event-utils';
import { wait } from '../../services/utils';

/** Dedupes parallel autodialer inits (e.g. React Strict Mode double-mount). */
const autodialerInitByLinkId = new Map<string, Promise<boolean>>();

export const BookingPage = () => {
  const { notification } = App.useApp();
  const [caseInfo, setCaseInfo] = useState<CaseInfo | null>(defaultCaseInfo);
  const [cancelledSlot, setCancelledSlot] = useState<Slot | null>(null);
  const [error, setError] = useState<any>();
  const { instance, inProgress, accounts } = useMsal();
  const [isOnline, setIsOnline] = useState<boolean>(false);
  const [displayConfirmationPage, setDisplayConfirmationPage] = useState<boolean>(false);
  const [bookedSlot, setBookedSlot] = useState<Slot>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [retryCountdown, setRetryCountdown] = useState<number>();
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const sttRootUrl = (process as any).env.REACT_APP_STT_ROOT_URL;
  const [caseStatus, setCaseStatus] = useState<CaseStatus>(CaseStatus.None);
  const [inputCaseComments, setInputCaseComments] = useState<string>('');
  const [lastPersistedComment, setLastPersistedComment] = useState<string>('');
  const salesChannel = useSalesChannelState(useShallow((state) => caseInfo?.salesChannel ?? state.salesChannel));
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [recordingComplete, setRecordingComplete] = useState<boolean>(false);
  const [bookingButtonStyle, setBookingButtonStyle] = useState<string | undefined>(undefined);
  const [activePowerHourConfig, setActivePowerHourConfig] = useState<PowerHourConfig | null>(null);
  const [callId, setCallId] = useState<string>('');
  const [isOpeningNextCase, setIsOpeningNextCase] = useState(false);
  const withGuard = useSharedLoadingGuard();
  dayjs.extend(utc);

  const userShouldBeRecorded = HasRole(accounts, Role.Recorded);
  const isTalkDeskAgent = HasRole(accounts, Role.TalkdeskAgent);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAutodialerFlow = searchParams.get('type') === 'autodialer';
  const autodialerInternalId = searchParams.get('id') ?? '';
  const inboundCenter = location?.state?.inboundCenter;
  const inboundAutodialed = location?.state?.isAutodialed === true || isAutodialerFlow;
  const navigate = useNavigate();

  const shouldSkipRecordingForStatus = (status: CaseStatus) =>
    isAutodialerFlow ? status === CaseStatus.WrongNumber : status === CaseStatus.NotReached || status === CaseStatus.WrongNumber;

  const handleCallClick = async (phoneNumber: string) => {
    if (isTalkDeskAgent) {
      await withGuard(async () => {
        const accessToken = (await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi)).accessToken;
        // Ensure phone number has German country code
        if (phoneNumber.startsWith('0')) {
          phoneNumber = '+49' + phoneNumber.substring(1);
        } else if (phoneNumber.startsWith('49')) {
            phoneNumber = '+49' + phoneNumber.substring(2);
        } else if (!phoneNumber.startsWith('+49')) {
          phoneNumber = '+49' + phoneNumber;
        }
        const body: CallBodyRequest = {
          customerPhoneNumber: phoneNumber,
          customerId: caseInfo?.globalCustomerId!,
        };
        const startCallResponse = await bookingPageService.startCallAsync(caseInfo?.opportunityId!, body, accessToken);
        setCallId(startCallResponse.callId);
      });
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.location.href = `tel:${phoneNumber}`;
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
  };

  //register the event listener that stops users from refreshing the page if they have data in it
  useEffect(() => {
    if (!inboundCenter && !isAutodialerFlow) {
      if (isOnline) {
        window.addEventListener('beforeunload', handleBeforeUnload);
      } else {
        // TODO: Investigate why it does not remove the event listener on going offline (isOnline = false)
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    }
  }, [isOnline, inboundCenter, isAutodialerFlow]);

  useEffect(() => {
    if (inboundCenter) {
      onOnlineChanged(inboundCenter);
    }
  }, [inboundCenter]);

  const goToBookingOffline = async () => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      if (caseInfo?.internalId) {
        await bookingPageService.releaseCase(caseInfo.internalId, salesChannel!, token.accessToken);
      }
    } catch {
      // Case may already be released after close
    }
    if (userShouldBeRecorded) {
      mediaRecorder.current?.stop();
      mediaRecorder.current = null;
      setAudioChunks([]);
    }
    setCaseStatus(CaseStatus.None);
    setDisplayConfirmationPage(false);
    setBookedSlot(undefined);
    setCaseInfo(null);
    setInputCaseComments('');
    setLastPersistedComment('');
    setIsOnline(false);
    setError(undefined);
    navigate('/booking', { replace: true });
  };

  const finishAutodialerSession = async (caseComment?: string) => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    if (caseComment && caseComment !== lastPersistedComment && caseInfo?.internalId) {
      await bookingPageService.addCaseComment(caseInfo.internalId, caseComment, salesChannel, token.accessToken);
    }
    setInputCaseComments('');
    setLastPersistedComment('');
    if (userShouldBeRecorded) {
      setCaseStatus(CaseStatus.Booked);
      mediaRecorder.current?.requestData();
      await wait(1);
    }
    notification.success({
      message: strings.inboundCallBack.success,
      duration: 3,
    });
    await goToBookingOffline();
  };

  const applyAssignedCase = async (assignedCase: CaseInfo) => {
    setIsOnline(true);
    setCaseInfo(assignedCase);
    setInputCaseComments('');
    setLastPersistedComment('');
    if (assignedCase.bookingStatus === 'ReschedulingNeeded') {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const cancelledSlotResponse = await salesAppointmentsService.getLatestSlot(token.accessToken, assignedCase.opportunityId, salesChannel);
      if (cancelledSlotResponse.isSuccess) {
        setCancelledSlot(cancelledSlotResponse.data);
      } else {
        setCancelledSlot(null);
      }
    } else {
      setCancelledSlot(null);
    }
  };

  const matchesAutodialerLinkId = (info: CaseInfo) => {
    const linkId = autodialerInternalId.trim();
    return (info.internalId ?? '').trim() === linkId;
  };

  const formatApiError = (error: unknown): string => {
    if (error === null || error === undefined) return '';
    if (typeof error === 'string') return error;
    if (typeof error === 'object') {
      const record = error as Record<string, unknown>;
      if (record.Message) return String(record.Message);
      if (record.message) return String(record.message);
    }
    return String(error);
  };

  const toErrorList = (errors: unknown): string[] => {
    if (!errors) return [];
    if (Array.isArray(errors)) return errors.map((e) => formatApiError(e));
    return [formatApiError(errors)];
  };

  const runInitializeAutodialerFlow = async (): Promise<boolean> => {
    if (!autodialerInternalId.trim()) {
      setError(strings.autodialerMissingId);
      return false;
    }

    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);

    const tryApplyMatchingAssignedCase = async (): Promise<boolean> => {
      const assignedCaseResult = await bookingPageService.getAssignedCase(token.accessToken, salesChannel!);
      if (assignedCaseResult.isSuccess && matchesAutodialerLinkId(assignedCaseResult.data)) {
        await applyAssignedCase(assignedCaseResult.data);
        return true;
      }
      return false;
    };

    if (await tryApplyMatchingAssignedCase()) {
      return true;
    }

    // Release a different active assignment (e.g. leftover booking-queue case) so this autodialer link can proceed.
    const existingAssignment = await bookingPageService.getAssignedCase(token.accessToken, salesChannel!);
    if (existingAssignment.isSuccess && !matchesAutodialerLinkId(existingAssignment.data)) {
      await bookingPageService.releaseCase(
        existingAssignment.data.internalId,
        existingAssignment.data.salesChannel ?? salesChannel!,
        token.accessToken,
      );
    }

    const body: OpportunityAssignDto = { internalId: autodialerInternalId.trim(), isAutodialerFlow: true };
    const assignResult = await bookingPageService.assignInboundOpportunity(body, token.accessToken);
    const assignErrors = toErrorList(assignResult.errors);

    if (assignResult.isSuccess || assignErrors.includes('ASSIGNMENT_EXISTS')) {
      if (await tryApplyMatchingAssignedCase()) {
        return true;
      }
      if (assignErrors.includes('ASSIGNMENT_EXISTS')) {
        setError(strings.autodialerAssignmentExistsOtherCase);
        return false;
      }
    }

    // Assign may have succeeded but response failed (e.g. duplicate parallel request returned 500).
    if (await tryApplyMatchingAssignedCase()) {
      return true;
    }

    if (assignErrors.some((e) => e.includes('NOT_AUTODIALED_CASE'))) {
      setError(strings.autodialerNotAutodialedCase);
    } else if (assignErrors.some((e) => e.includes('CASE_NOT_FOUND'))) {
      setError(strings.autodialerCaseNotFound);
    } else {
      setError(assignErrors.join(', ') || strings.autodialerAssignFailed);
    }
    return false;
  };

  const initializeAutodialerFlow = (): Promise<boolean> => {
    const cacheKey = autodialerInternalId.trim().toLowerCase();
    const existing = autodialerInitByLinkId.get(cacheKey);
    if (existing) {
      return existing;
    }

    const promise = runInitializeAutodialerFlow().catch((err) => {
      autodialerInitByLinkId.delete(cacheKey);
      throw err;
    });
    autodialerInitByLinkId.set(cacheKey, promise);
    return promise;
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      if (isAutodialerFlow) {
        try {
          await initializeAutodialerFlow();
        } catch (e: any) {
          setError(e?.message ?? strings.autodialerAssignFailed);
        }
        setIsLoading(false);
        return;
      }

      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const assignedCaseResult = await bookingPageService.getAssignedCase(token.accessToken, salesChannel!);
      if (assignedCaseResult.isSuccess) {
        await applyAssignedCase(assignedCaseResult.data);
      }
      setIsLoading(false);
    })();
  }, []);

  // Handle audio recording
  useEffect(() => {
    if (userShouldBeRecorded && isOnline && caseStatus === CaseStatus.None) {
      setAudioChunks([]);
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          const recorder = new MediaRecorder(stream);
          recorder.start();
          const chunks = [] as Blob[];

          recorder.ondataavailable = (event: BlobEvent) => {
            chunks.push(event.data);
          };

          recorder.onstop = () => {
            setAudioChunks([]);
            stream.getTracks().forEach((track) => track.stop());
            setAudioChunks(chunks);
            setRecordingComplete(true);
          };

          mediaRecorder.current = recorder;
        })
        .catch((error) => {
          console.error('Error accessing microphone:', error);
        });
    }

    if (userShouldBeRecorded && caseStatus !== CaseStatus.None && !shouldSkipRecordingForStatus(caseStatus)) {
      mediaRecorder.current?.stop();
    }
  }, [caseStatus, isOnline, isAutodialerFlow]);

  // Fetch active power hour config
  useEffect(() => {
    (async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const activePowerHourConfig = await reportingService.getActivePowerHourConfig(token.accessToken);
      setActivePowerHourConfig(activePowerHourConfig);
    })();
  }, [caseInfo]);

  // Handle sending audio recording when complete
  useEffect(() => {
    if (recordingComplete && caseStatus !== CaseStatus.None && !shouldSkipRecordingForStatus(caseStatus)) {
      handleStopRecording(getCaseStatusString(caseStatus), salesChannel!, caseInfo?.opportunityId, audioChunks);
      setRecordingComplete(false);
      setAudioChunks([]);
      setCaseStatus(CaseStatus.None);
    }
  }, [audioChunks, recordingComplete]);

  const fireConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });
  };

  function getCaseStatusString(status: CaseStatus): string {
    switch (status) {
      case CaseStatus.None:
        return 'None';
      case CaseStatus.Booked:
        return 'Booked';
      case CaseStatus.ReachedButNotBooked:
        return 'ReachedButNotBooked';
      case CaseStatus.NotReached:
        return 'NotReached';
      case CaseStatus.CustomerLost:
        return 'CustomerLost';
      case CaseStatus.WrongNumber:
        return 'WrongNumber';
      default:
        return 'Unknown';
    }
  }

  const handleCaseAssignment = async (assignedCaseResult: Result<CaseInfo>) => {
    setCaseStatus(CaseStatus.None);
    if (assignedCaseResult.isSuccess) {
      const assignedCase = assignedCaseResult.data;
      setCaseInfo(assignedCase);
      setInputCaseComments('');
      setLastPersistedComment('');
      if (assignedCase.bookingStatus === 'ReschedulingNeeded') {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const cancelledSlotResponse = await salesAppointmentsService.getLatestSlot(token.accessToken, assignedCase.opportunityId, salesChannel);
        if (cancelledSlotResponse.isSuccess) {
          setCancelledSlot(cancelledSlotResponse.data);
        } else {
          setCancelledSlot(null);
        }
      } else {
        setCancelledSlot(null);
      }
    } else if (assignedCaseResult.errors && assignedCaseResult.errors[0].includes('NO_CASE_AVAILABLE')) {
      const errorDeserialized = JSON.parse(assignedCaseResult.errors[0]);
      const errorDate = dayjs(errorDeserialized.RetryIn).format('YYYY-MM-DDTHH:mm:ss');
      // Get the current UTC time
      const nowUtc = dayjs().utc().format('YYYY-MM-DDTHH:mm:ss');
      // Calculate the difference in milliseconds
      const differenceInSeconds = dayjs(errorDate).diff(nowUtc, 'milliseconds');
      setRetryCountdown(differenceInSeconds);
      setCaseInfo(null);
    } else {
      setCaseInfo(null);
      throw assignedCaseResult.errors;
    }
  };

  const handleStopRecording = async (bookingStatus: string | undefined, salesChannel: string, opportunityId: string | undefined, chunksToSend: Blob[]) => {
    if (isOnline && bookingStatus && typeof opportunityId === 'string' && chunksToSend.length > 0) {
      const audioBlob = new Blob(chunksToSend, { type: 'audio/wav' });
      const arrayBuffer = await audioBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      try {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const locale = 'de-DE';
        fetch(`${sttRootUrl}/api/v1/calls/transcriptions`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
            'enpal-user-id': token.account.localAccountId,
            'enpal-correlation-id': '',
            'enpal-opportunity-id': opportunityId,
            'enpal-language-locale': locale,
            'enpal-booking-state': bookingStatus,
            'enpal-sales-channel': salesChannel,
            'Content-Type': 'application/octet-stream',
          },
          body: uint8Array,
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  /**
   * Handles the online changing on the header
   * @param isOnline whether the online flag is true or false on the header
   */
  const onOnlineChanged = async (isOnline: boolean) => {
    await withGuard(async () => {
      try {
        setCaseStatus(CaseStatus.None);
        setError(undefined);
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        // If the sales channel is DigitalSales and the user is not an admin, then set the sales channel to the role based sales channel
        // This is to ensure that the user can only book cases for their own sales channel
        if (isOnline) {
          const assignedCaseResult = await bookingPageService.getOrAssignPriorityCase(token.accessToken!, salesChannel!);
          handleCaseAssignment(assignedCaseResult);
        } else {
          if (inputCaseComments && inputCaseComments !== lastPersistedComment && caseInfo?.internalId) {
            await bookingPageService.addCaseComment(caseInfo.internalId, inputCaseComments, salesChannel, token.accessToken);
          }
          setInputCaseComments('');
          setLastPersistedComment('');
          if (userShouldBeRecorded) {
            mediaRecorder.current?.requestData();
            mediaRecorder.current?.stop();
            mediaRecorder.current = null;
            setAudioChunks([]);
          }
          setDisplayConfirmationPage(false);
          await bookingPageService.releaseCase(caseInfo?.internalId ?? '', salesChannel, token?.accessToken!);
          setCaseInfo(null);
        }
        setIsOnline(isOnline);
      } catch (e: any) {
        setError(e);
        throw e;
      }
    }, setIsLoading);
  };

  const onTryFetchNextCase = async () => {
    if (inboundCenter !== undefined || isAutodialerFlow) {
      return;
    }

    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const assignedCaseResult = await bookingPageService.getOrAssignPriorityCase(token.accessToken!, salesChannel!);
      handleCaseAssignment(assignedCaseResult);
    } catch (e) {
      setError(e);
    }
  };

  const onRefreshCountdown = async () => {
    await withGuard(async () => {
      try {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const updateCaseResult = await bookingPageService.updateCaseAssignment(caseInfo?.internalId!, salesChannel, token.accessToken!);
        if (updateCaseResult.isSuccess) {
          setCaseInfo({ ...caseInfo!, expireAt: updateCaseResult.data });
        }
      } catch (e) {
        setError(e);
      }
    });
  };

  const customizeUserInterface = (user: UserDataDto) => {
    if (user.selectedBookingButton) {
      setBookingButtonStyle(user.selectedBookingButton);
    }
  };

  const header = (
    <Header
      changeUserInterface={customizeUserInterface}
      optionalComponent={
        <HeaderBookingPage
          inboundCenter={inboundCenter}
          isAutodialerFlow={isAutodialerFlow}
          isOnline={isOnline}
          caseId={caseInfo?.caseId!}
          salesChannel={salesChannel}
          caseExpiration={displayConfirmationPage ? undefined : caseInfo?.expireAt}
          onOnlineChanged={onOnlineChanged}
          onRefreshCountdown={onRefreshCountdown}
          isLoading={isLoading}
        />
      }
    />
  );

  const onBackToInbound = async (isSuccessful: boolean) => {
    await onOnlineChanged(false);
    navigate('/inbound', {
      state: {
        isSuccess: isSuccessful,
      },
    });
    setBookedSlot(undefined);
  };

  const confirmationPageOpenNextCase = async (caseComment: string | undefined) => {
    if (isAutodialerFlow) {
      await withGuard(async () => {
        await finishAutodialerSession(caseComment);
      }, setIsOpeningNextCase);
      return;
    }

    await withGuard(async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      if (caseComment && caseComment !== lastPersistedComment && caseInfo?.internalId) {
        await bookingPageService.addCaseComment(caseInfo.internalId, caseComment, salesChannel, token.accessToken);
      }
      setInputCaseComments('');
      setLastPersistedComment('');
      const nextCase = await bookingPageService.getOrAssignPriorityCase(token.accessToken, salesChannel);

      // send recording for booked cases
      if (userShouldBeRecorded) {
        setCaseStatus(CaseStatus.Booked);
        mediaRecorder.current?.requestData();
      }

      await wait(1);

      handleCaseAssignment(nextCase);

      setDisplayConfirmationPage(false);
    }, setIsOpeningNextCase);
  };

  if (error) {
    return (
      <>
        {header}
        <div className="container">
          <Alert type="error" message={strings.errorOccurred} description={error?.message ?? error}></Alert>
        </div>
      </>
    );
  }

  const openErrorNotification = (isCaseNotFound: boolean) => {
    notification.open({
      key: 'errorNotification',
      message: strings.errorOccurred,
      description: (
        <>
          <p>{isCaseNotFound ? strings.caseNotFoundErrorDescription : strings.genericCloseCaseErrorDescription}</p>
          <p>{strings.youCanGetANewCase}</p>
          <Button
            type="primary"
            onClick={async (_) => {
              setIsLoading(true);
              try {
                notification.destroy('errorNotification');
                await confirmationPageOpenNextCase(undefined);
                await wait(0.5);
              } catch (e: any) {
                setCaseInfo(null);
                setError(e);
              } finally {
                setIsLoading(false);
              }
            }}
          >
            {strings.getNewCase}
          </Button>
        </>
      ),
      placement: 'topRight',
      duration: 0,
      type: 'error',
    });
  };

  const handleCaseClosed = async (caseComplete: OpportunityComplete, bookedSlot?: Slot) => {
    await withGuard(async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      // Always persist manual comment BEFORE closing the case if present.
      if (inputCaseComments && caseInfo?.internalId) {
        await bookingPageService.addCaseComment(caseInfo.internalId, inputCaseComments, salesChannel, token.accessToken);
        setLastPersistedComment(inputCaseComments);
      }

      const closeCaseResult = await bookingPageService.closeCase(caseComplete, caseInfo?.salesChannel!, token.accessToken);

      if (!closeCaseResult.isSuccess) {
        const isCaseNotFound = closeCaseResult.errors?.includes('CASE_NOT_FOUND');
        openErrorNotification(isCaseNotFound!);
        setCaseInfo(null);
        throw new Error(closeCaseResult.errors?.join(', ') || 'Failed to close case');
      }

      if (caseComplete.caseStatus === CaseStatus.Booked) {
        //If no booked slot is provided, then there was an error with the booking. Open the error notification that allows the user to get a new assignment case
        if (!bookedSlot) {
          openErrorNotification(true);
          return;
        }

        fireConfetti();

        setBookedSlot(bookedSlot);
        setDisplayConfirmationPage(true);
      } else {
        setInputCaseComments('');
        setLastPersistedComment('');
        if (userShouldBeRecorded) {
          setCaseStatus(caseComplete.caseStatus);
          mediaRecorder.current?.requestData();
        }
        await wait(1);

        if (isAutodialerFlow) {
          await goToBookingOffline();
        } else if (inboundCenter) {
          await onBackToInbound(closeCaseResult.isSuccess);
        } else {
          const nextCase = await bookingPageService.getOrAssignPriorityCase(token.accessToken, salesChannel);
          handleCaseAssignment(nextCase);
          if (nextCase.isSuccess) {
            notification.open({
              message: strings.caseCompleteNotification,
              placement: 'topRight',
              type: 'success',
            });
          } else {
            notification.open({
              message: strings.assignNewCaseError,
              placement: 'topRight',
              type: 'error',
            });
          }
        }
      }
    });
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
        <div className="loading-text" style={{ marginTop: '2vh' }}>
          Loading...
        </div>
      </div>
    );
  }

  let scripts;

  if (caseInfo) {
    scripts = <CaseScript salesScript={caseInfo.salesScript} salesObjections={caseInfo.salesObjections} resetPosition={!displayConfirmationPage} />;
  }

  if (displayConfirmationPage) {
    return (
      <>
        {header}
        <div className="bp-top-microphone">
          <OnlineRecordingBookingPage caseInfo={caseInfo} isAgentOnline={isOnline} isUserRecorded={userShouldBeRecorded} />
        </div>
        <div className="bp-layout">
          <div className="bp-main">
            <ConfirmationPage
              inboundCenter={inboundCenter}
              isAutodialerFlow={isAutodialerFlow}
              onBackToInbound={onBackToInbound}
              caseInfo={caseInfo!}
              bookedSlot={bookedSlot!}
              cancelledSlot={cancelledSlot}
              inputCaseComments={inputCaseComments}
              setInputCaseComments={setInputCaseComments}
              onOpenNextCase={confirmationPageOpenNextCase}
              isOpeningNextCase={isOpeningNextCase}
              handleCallButtonClick={handleCallClick}
            />
          </div>
          <div className="bp-sider">{scripts}</div>
        </div>
      </>
    );
  }

  if (isOnline && caseInfo) {
    return (
      <>
        {header}
        <div className="bp-vertical-allignment">
          {activePowerHourConfig && <PowerHourBar powerHourConfig={activePowerHourConfig!} />}
          <div className="bp-top-microphone">
            <OnlineRecordingBookingPage caseInfo={caseInfo} isAgentOnline={isOnline} isUserRecorded={userShouldBeRecorded} />
          </div>
          <div className="bp-layout">
            <div className="bp-main">
              <OnlineBookingPage
                inboundCenter={inboundCenter}
                inboundAutodialed={inboundAutodialed}
                isAutodialerFlow={isAutodialerFlow}
                caseInfo={caseInfo}
                setCaseInfo={setCaseInfo}
                cancelledSlot={cancelledSlot}
                onCaseClosed={handleCaseClosed}
                inputCaseComments={inputCaseComments}
                setInputCaseComments={setInputCaseComments}
                bookingButtonStyle={bookingButtonStyle}
                handleCallButtonClick={handleCallClick}
              />
            </div>
            <div className="bp-sider">{scripts}</div>
          </div>
        </div>
      </>
    );
  }

  if (isOnline && caseInfo === null && isLoading === false) {
    return (
      <>
        {header}
        <OnlineBookingNoCasesPage onTryFetchNextCase={onTryFetchNextCase} retryCountdown={retryCountdown!} />
      </>
    );
  }

  if (!isOnline && !isAutodialerFlow) {
    return (
      <>
        {header}
        <OfflineBookingPage />
      </>
    );
  }

  return (
    <>
      {header}
      <Alert type="error" message="The application reached an unexpected state, please contact support." />
    </>
  );
};
